import React, { useCallback, useContext, useMemo } from 'react';
import { Button, Typography } from 'antd';
import { FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';

import { ICityTaxFile } from '../ViewCityTax/useViewCityTax';
import privateHttpClient from '../../../services/http/privateHttpClient';
import { EmitError } from '../../../utils/EmitError';
import { ThemeContext } from '../../../contexts/ThemeContext';

const { Text } = Typography;

interface IProps {
    document: ICityTaxFile;
}

export default function PDFItem({ document }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const filename = useMemo(() => {
        return document.url.split('/').slice(-1)[0] || 'documento.pdf';
    }, [document]);

    const viewPDF = useCallback(async () => {
        const controller = new AbortController();

        try {
            const response = await privateHttpClient.get(document.url, {
                responseType: 'arraybuffer',
                headers: {
                    Accept: 'application/pdf',
                },
                signal: controller.signal,
            });

            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }
            EmitError(err, true, 'viewCityTax_pdf_error');
        }

        return () => {
            controller.abort();
        };
    }, [document]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: 180,
                minHeight: 180,
                padding: '18px 14px',
                backgroundColor: isDark ? '#1f1f1f' : '#f8fafc',
                border: `1px solid ${isDark ? '#303030' : '#e2e8f0'}`,
                borderRadius: 10,
                gap: 12,
                transition: 'all 0.2s',
            }}
        >
            <div
                style={{
                    width: 52,
                    height: 52,
                    borderRadius: 10,
                    backgroundColor: isDark ? '#381e1e' : '#fee2e2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <FilePdfOutlined style={{ fontSize: 26, color: '#ef4444' }} />
            </div>

            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                }}
            >
                <Text
                    style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: isDark ? '#f0f0f0' : '#1e293b',
                        maxWidth: 150,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={filename}
                >
                    {filename}
                </Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                    Documento PDF
                </Text>
            </div>

            <Button
                type="default"
                size="small"
                icon={<EyeOutlined />}
                onClick={viewPDF}
                style={{
                    borderRadius: 6,
                    fontWeight: 500,
                    width: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                }}
            >
                Visualizar PDF
            </Button>
        </div>
    );
}